import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, MenuItem, TextField, Typography,
} from '@mui/material';
import {
  RefreshRounded as RefreshIcon,
  PlayCircleOutlineRounded as RunningIcon,
  TaskAltRounded as DoneIcon,
  ReportProblemOutlined as EscalatedIcon,
  BoltOutlined as PulseIcon,
} from '@mui/icons-material';
import WorkflowService, { WorkflowInstance } from '../../api/services/workflow.service';
import { instanceTone, isEscalated, waitingOn, buildPulse, PulseRow } from './workflowUI';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import {
  NavPanel, PanelButton, PanelGroup, } from '../../components/layout/NavPanel';
import { status as statusTokens } from '../../theme';

// The control room.
//
// Three things changed beyond colour. The screen had no title at all, so it
// was the one tab that never said what it was. Its filters sat in the
// content area above the table, which is where a filter is easiest to miss
// and hardest to keep — they are in the sidebar panel now, next to the
// views they modify, the same place Files keeps its categories. And the
// activity rail was a sticky 320px column stealing width from the table on
// every screen size; it is under the table now, where it does not compete
// with the thing you came to read.

type View = 'active' | 'finished';

const Operations: React.FC = () => {
  const navigate = useNavigate();

  const [view, setView] = useState<View>('active');
  const [rows, setRows] = useState<WorkflowInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [areaFilter, setAreaFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');

  const load = useCallback(async (which: View) => {
    setLoading(true);
    try {
      setError(null);
      setRows(await WorkflowService.getInstances(which));
      setUpdatedAt(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(view); }, [load, view]);

  const areas = useMemo(
    () => Array.from(new Set(rows.map((r) => r.serviceAreaId).filter((a) => a != null))).sort() as number[],
    [rows],
  );
  const templates = useMemo(
    () => Array.from(new Set(rows.map((r) => r.templateName))).sort(),
    [rows],
  );

  // Filter, then sort escalated-first — attention rises to the top.
  const visible = useMemo(() => {
    let list = rows;
    if (areaFilter !== 'all') list = list.filter((r) => String(r.serviceAreaId) === areaFilter);
    if (templateFilter !== 'all') list = list.filter((r) => r.templateName === templateFilter);
    return [...list].sort((a, b) => {
      const ae = isEscalated(a);
      const be = isEscalated(b);
      if (ae !== be) return ae ? -1 : 1;
      return new Date(b.updatedAt || b.startedAt).getTime() - new Date(a.updatedAt || a.startedAt).getTime();
    });
  }, [rows, areaFilter, templateFilter]);

  const needsAttention = rows.filter(isEscalated).length;
  const pulse = useMemo(() => buildPulse(rows), [rows]);
  const filtered = areaFilter !== 'all' || templateFilter !== 'all';

  const columns: Column<WorkflowInstance>[] = [
    {
      key: 'label',
      header: 'Workflow',
      width: 260,
      sortValue: (r) => r.label,
      filter: 'text',
      render: (r) => (
        <>
          <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
          {r.context?.name && !r.label.includes(r.context.name) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.context.name}</Typography>
          )}
        </>
      ),
    },
    // No Template column: every workflow's name starts with its template
    // ("Medical Certificate Renewal · Oct 20"), and the side panel filters
    // by it. Saying it twice cost the table its last columns.
    {
      key: 'area',
      hide: 'phone',
      header: 'Area',
      numeric: true,
      render: (r) => <Typography variant="body2" color="text.secondary">{r.serviceAreaId ?? '—'}</Typography>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const tone = instanceTone(r);
        return <ToneChip tone={tone.tone} label={tone.label} />;
      },
    },
    {
      key: 'waiting',
      header: 'Waiting on',
      width: 260,
      render: (r) => (
        <Typography variant="body2" sx={{ color: isEscalated(r) ? 'error.main' : 'text.secondary' }}>
          {waitingOn(r)}
        </Typography>
      ),
    },
    {
      key: 'progress',
      hide: 'tablet',
      header: 'Progress',
      align: 'right',
      numeric: true,
      render: (r) => {
        const done = r.steps.filter((s) => s.status === 'completed').length;
        const total = r.steps.filter((s) => s.type !== 'conditional').length;
        return (
          <Typography variant="body2" color="text.secondary">{done}/{total}</Typography>
        );
      },
    },
    {
      key: 'started',
      hide: 'phone',
      header: 'Started',
      render: (r) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(r.startedAt).toLocaleString([], {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          })}
        </Typography>
      ),
    },
    {
      key: 'origin',
      hide: 'phone',
      header: 'Origin',
      render: (r) => (
        <Typography variant="body2" color="text.secondary">
          {r.triggerType === 'manual' ? 'by hand' : r.triggerType}
        </Typography>
      ),
    },
  ];

  return (
    <>
      {/* ------------------------------------------------------- panel */}
      <NavPanel>
        <PanelGroup label="Views">
          <PanelButton
            icon={<RunningIcon />}
            label="Running"
            count={view === 'active' ? rows.length : undefined}
            active={view === 'active'}
            onClick={() => setView('active')}
          />
          <PanelButton
            icon={<DoneIcon />}
            label="Done"
            count={view === 'finished' ? rows.length : undefined}
            active={view === 'finished'}
            onClick={() => setView('finished')}
          />
        </PanelGroup>

        <PanelGroup label="Filter">
          <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <TextField
              select fullWidth label="Area" value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
            >
              <MenuItem value="all">All areas</MenuItem>
              {areas.map((a) => <MenuItem key={a} value={String(a)}>Area {a}</MenuItem>)}
            </TextField>
            <TextField
              select fullWidth label="Template" value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
            >
              <MenuItem value="all">All templates</MenuItem>
              {templates.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {filtered && (
              <Button
                size="small"
                onClick={() => { setAreaFilter('all'); setTemplateFilter('all'); }}
                sx={{ alignSelf: 'flex-start' }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </PanelGroup>

        {/* Kept in the panel deliberately: it is peripheral awareness, not
            something you read row by row, so it belongs beside the work
            rather than in a column competing with the table. */}
        <PanelGroup label="Engine activity">
          <Box sx={{ px: 1.5 }}>
            {pulse.length === 0 ? (
              <Typography variant="caption" color="text.disabled">Quiet right now.</Typography>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {pulse.slice(0, 12).map((e, i) => <PulseRow key={i} e={e} />)}
              </Box>
            )}
          </Box>
        </PanelGroup>
      </NavPanel>

      {/* -------------------------------------------------------- page */}
      <PageBody>
        <PageHeader
          title="Operations"
          subtitle={
            view === 'active'
              ? `${rows.length} running${needsAttention ? ` · ${needsAttention} need${needsAttention === 1 ? 's' : ''} attention` : ''}`
              : `${rows.length} finished`
          }
          actions={
            <>
              {updatedAt && (
                <Typography variant="caption" color="text.disabled">
                  Updated {updatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </Typography>
              )}
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => load(view)} disabled={loading}>
                Refresh
              </Button>
            </>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {needsAttention > 0 && view === 'active' && (
          <Card sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.25, borderColor: 'error.light', bgcolor: 'error.light' }}>
            <EscalatedIcon sx={{ color: 'error.dark', fontSize: 19 }} />
            <Typography variant="body2" sx={{ color: 'error.dark', fontWeight: 600 }}>
              {needsAttention} run{needsAttention === 1 ? '' : 's'} escalated and waiting on a person.
            </Typography>
          </Card>
        )}

        <DataTable
          twoLine
          columns={columns}
          rows={visible}
          rowKey={(r) => r._id}
          loading={loading}
          onRowClick={(r) => navigate(`/operations/${r._id}`)}
          rowAccent={(r) => (isEscalated(r) ? statusTokens.danger : undefined)}
          empty={
            <EmptyState
              compact
              icon={<PulseIcon />}
              title={filtered ? 'Nothing matches those filters' : view === 'active' ? 'Nothing running' : 'Nothing finished yet'}
              description={
                filtered
                  ? 'Clear the area or template filter in the sidebar to see everything.'
                  : view === 'active'
                    ? 'Workflows started by a schedule or by hand appear here while they are in progress.'
                    : 'Completed and cancelled runs will collect here.'
              }
              action={
                filtered
                  ? { label: 'Clear filters', onClick: () => { setAreaFilter('all'); setTemplateFilter('all'); } }
                  : undefined
              }
            />
          }
        />

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          Click a row to open the run. Escalated runs are striped and sorted first.
        </Typography>
      </PageBody>
    </>
  );
};

export default Operations;
